import React from "react";
import {Box, Text} from "@mantine/core";
import {IVasPainScoreStep, IProtocolStepStatus} from "../../../types/protocol";
import {IVasPainScoreStepDefinition} from "../../../types/protocolDefinitions";
import stepClasses from "../StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import classes from "./VasPainScoreStepView.module.css";
import cx from "clsx";
import dayjs from "dayjs";
import {TWorkflow} from "../StepView";

type VasPainScoreStep = IVasPainScoreStep | IVasPainScoreStepDefinition;

export interface VasPainScoreStepViewProps {
    step: VasPainScoreStep;
    timezone?: string;
    onChange: (updates: DeepPartial<VasPainScoreStep>) => void;
    workflow?: TWorkflow;
}

interface PainLevel {
    score: number;
    emoji: string;
    label: string;
    color: string;
    ml?: string
    mr?: string
}


const PAIN_LEVELS: PainLevel[] = [
    {score: 0, emoji: "😊", label: "No Pain", color: "rgb(22 163 74)"},
    {score: 1, emoji: "🙂", label: "Mild", color: "rgb(34 197 94)"},
    {score: 2, emoji: "🙂", label: "Mild", color: "rgb(132 204 22)"},
    {score: 3, emoji: "😐", label: "Mild", color: "rgb(163 230 53)"},
    {score: 4, emoji: "😐", label: "Moderate", color: "rgb(250 204 21)"},
    {score: 5, emoji: "😕", label: "Moderate", color: "rgb(234 179 8)"},
    {score: 6, emoji: "🙁", label: "Moderate", color: "rgb(251 146 60)"},
    {score: 7, emoji: "🙁", label: "Severe", color: "rgb(249 115 22)"},
    {score: 8, emoji: "😢", label: "Severe", color: "rgb(234 88 12)"},
    {score: 9, emoji: "😢", label: "Very Severe", color: "rgb(239 68 68)"},
    {score: 10, emoji: "😭", label: "Worst", color: "rgb(220 38 38)"},
];

export const VasPainScoreStepView: React.FC<VasPainScoreStepViewProps> = ({
    step,
    timezone,
    onChange,
    workflow = "session",
}) => {
    const isReadOnly = workflow === "review";
    const currentScore = step.score;

    const onSelected = (score: number) => {
        switch (workflow) {
            case "session":
                onChange({score: score, startTime: dayjs().tz(timezone!, false), status: IProtocolStepStatus.COMPLETED})
                break;
            case "review_edit":
                // in review edit mode we only want to update the score not start_time or status
                onChange({score: score});
                break;
            default:
                break;
        }
    }

    const getPainLabel = (score: number | undefined): string => {
        if (score === undefined) return "";
        const level = PAIN_LEVELS.find(p => p.score === score);
        return level?.label ?? "";
    };
    const renderPainBoxes = () => {
        return PAIN_LEVELS.map((level) => {
            const isSelected = currentScore === level.score;
            return (
                <Box key={level.score}>
                    <Box
                         onClick={isReadOnly ? undefined : () => onSelected(level.score)}
                         style={{
                             borderColor: isSelected ? level.color : "",
                             cursor: isReadOnly ? "default" : "pointer",
                         }}
                         className={cx(classes.emojiItem, isSelected && classes.selected)}
                    >
                        <Text size={"2em"}>{level.emoji}</Text>
                        <Text
                            size="sm"
                            fw={isSelected ? 700 : "bold"}
                            className={classes.scoreLabel}
                        >
                            {level.score}
                        </Text>
                    </Box>
                    <Box
                        className={classes.colorBar}
                        style={{backgroundColor: level.color}}
                    />
                </Box>
            );
        })
    }

    return (
        <div style={{display: "flex", flexDirection: "column", width: "100%"}}>
            <Box>
            </Box>
            <Box className={stepClasses.stepViewField} style={{justifySelf: "center", gridColumn: "span 5"}}>
                <Text c={"dimmed"}>Click a face to record the pain level. The timestamp is captured when you select.</Text>
            </Box>

            <Box mb={"1em"} className={stepClasses.stepViewField}
                 style={{justifySelf: "center", gridColumn: "span 4"}}>
                <Text
                    size="lg"
                    fw={600}
                    ta="center"
                    mb="xs"
                    mih={"2em"}
                    className={classes.labelRow}
                >
                    {getPainLabel(currentScore)}
                </Text>

                <Box className={classes.emojiRow}>
                    <Box className={classes.painTextHeader}><Text className={cx(classes.painText)}>No Pain</Text></Box>
                    <Box className={classes.painTextHeader} style={{gridColumn: "span 3"}}><Text
                        className={cx(classes.painText)}>Mild</Text></Box>
                    <Box className={classes.painTextHeader} style={{gridColumn: "span 3"}}><Text
                        className={cx(classes.painText)}>Moderate</Text></Box>
                    <Box className={classes.painTextHeader} style={{gridColumn: "span 2"}}><Text
                        className={cx(classes.painText)}>Severe</Text></Box>
                    <Box className={classes.painTextHeader}><Text
                        className={cx(classes.painText)}>Very Severe</Text></Box>
                    <Box className={classes.painTextHeader}><Text className={cx(classes.painText)}>Worst</Text></Box>
                    {renderPainBoxes()}
                </Box>
            </Box>
        </div>
    );
};